/**
 * Twelvedata API Provider
 * Adapter for the existing Twelvedata service
 */

import {
  ApiProvider,
  Asset,
  AssetPrice,
  AssetType,
  TimeRange,
  HistoricalData,
  AssetSearchResult,
  AssetValidationResult,
  RequestConfig
} from '@/types/api';

import { twelvedataService } from '../twelvedata';
import { createApiError, categorizeHttpError, extractRetryAfter } from '../errors';

export class TwelvedataProvider implements ApiProvider {
  name = 'twelvedata';
  priority = 1; // 最高优先级，优先使用
  isHealthy = true;
  lastError?: Date;
  supportedAssetTypes: AssetType[] = ['crypto', 'stock', 'forex']; // 指数仅由 Financial Modeling Prep 支持
  
  private readonly timeout = 10000;
  
  /**
   * Get single asset price
   */
  async getAssetPrice(asset: Asset, config?: RequestConfig): Promise<AssetPrice> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), config?.timeout || this.timeout);
      
      if (config?.signal) {
        config.signal.addEventListener('abort', () => controller.abort());
      }
      
      let result: AssetPrice;
      
      try {
        switch (asset.type) {
          case 'crypto':
            const cryptoResults = await twelvedataService.getCryptoPrice([asset.id]);
            if (cryptoResults.length === 0) {
              throw new Error(`Crypto asset not found: ${asset.symbol}`);
            }
            const cryptoResult = cryptoResults[0];
            if (!cryptoResult) {
              throw new Error(`Crypto asset not found: ${asset.symbol}`);
            }
            result = cryptoResult;
            break;
            
          case 'stock':
            result = await twelvedataService.getStockPrice(asset.symbol);
            break;
            
          // 指数仅由 Financial Modeling Prep 支持
          // case 'index': 已移除
            
          case 'forex':
            // Parse forex symbol (e.g., "USD/EUR" or "usd_eur")
            const [from, to] = this.parseForexSymbol(asset.symbol);
            try {
              // Try the original direction first
              result = await twelvedataService.getForexRate(from, to);
            } catch (error) {
              // If original direction fails, try reverse direction
              try {
                const reverseResult = await twelvedataService.getForexRate(to, from);
                // Convert the price to the original direction (1 / reverse_price)
                result = {
                  ...reverseResult,
                  id: `${from.toLowerCase()}_${to.toLowerCase()}`,
                  symbol: `${from.toUpperCase()}/${to.toUpperCase()}`,
                  name: `${from.toUpperCase()}/${to.toUpperCase()}`,
                  current_price: reverseResult.current_price > 0 ? 1 / reverseResult.current_price : 0,
                  price_change_24h: reverseResult.price_change_24h !== 0 && reverseResult.current_price > 0
                    ? -reverseResult.price_change_24h / (reverseResult.current_price * reverseResult.current_price)
                    : 0,
                  price_change_percentage_24h: reverseResult.price_change_percentage_24h !== 0
                    ? -reverseResult.price_change_percentage_24h
                    : 0,
                };
              } catch (reverseError) {
                // If both directions fail, throw the original error
                throw error;
              }
            }
            break;
            
          default:
            throw createApiError(
              'VALIDATION_ERROR',
              `Unsupported asset type: ${asset.type}`,
              { provider: this.name }
            );
        }
        
        // Ensure the returned id matches the asset.id
        clearTimeout(timeoutId);
        return {
          ...result,
          id: asset.id,
        };
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    } catch (error) {
      throw this.handleError(error);
    }
  }
  
  /**
   * Get multiple asset prices
   */
  async getAssetPrices(assets: Asset[], config?: RequestConfig): Promise<AssetPrice[]> {
    try {
      const results: AssetPrice[] = [];
      let hasError = false;
      let lastError: any = null;
      
      // Group assets by type for efficient processing
      const assetsByType = this.groupAssetsByType(assets);
      
      // Process crypto assets in batch
      if (assetsByType.crypto.length > 0) {
        try {
          const cryptoIds = assetsByType.crypto.map(asset => asset.id);
          const cryptoResults = await twelvedataService.getCryptoPrice(cryptoIds);
          results.push(...cryptoResults);
        } catch (error) {
          console.warn('Failed to get crypto prices:', error);
          // If we were rate-limited (or auth failed), bubble up so UnifiedApi can failover/cooldown.
          const err: any = error;
          if (err && typeof err === 'object' && 'code' in err) {
            const code = (err as any).code;
            if (code === 'RATE_LIMIT_ERROR' || code === 'AUTH_ERROR') {
              throw error;
            }
          }
          hasError = true;
          lastError = error;
        }
      }
      
      // Process other asset types individually
      for (const asset of [...assetsByType.stock, ...assetsByType.index, ...assetsByType.forex]) {
        try {
          const result = await this.getAssetPrice(asset, config);
          results.push(result);
        } catch (error) {
          console.warn(`Failed to get price for ${asset.symbol}:`, error);
          // Important: if Twelvedata is rate-limited (often returned as HTTP 200 with JSON code=429),
          // do NOT swallow the error; let UnifiedApi trigger provider cooldown and failover to alternatives.
          const err: any = error;
          if (err && typeof err === 'object' && 'code' in err) {
            const code = (err as any).code;
            if (code === 'RATE_LIMIT_ERROR' || code === 'AUTH_ERROR') {
              throw error;
            }
          }
          hasError = true;
          lastError = error;
          // Continue with other assets
        }
      }
      
      // 如果所有请求都失败，抛出最后一个错误
      if (results.length === 0 && assets.length > 0 && hasError && lastError) {
        throw lastError;
      }
      
      return results;
    } catch (error) {
      throw this.handleError(error);
    }
  }
  
  /**
   * Get historical data
   */
  async getHistoricalData(
    asset: Asset,
    timeRange: TimeRange,
    config?: RequestConfig
  ): Promise<HistoricalData> {
    try {
      const days = this.timeRangeToDays(timeRange);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), config?.timeout || this.timeout);
      
      if (config?.signal) {
        config.signal.addEventListener('abort', () => controller.abort());
      }
      
      try {
        const rawData = await twelvedataService.getHistoricalData(asset.id, days);
        clearTimeout(timeoutId);
        
        // Transform to unified format
        const historicalData: HistoricalData = {
          asset,
          timeRange,
          data: {
            prices: rawData.prices.map(([timestamp, price]) => ({
              timestamp,
              value: price
            })),
            volumes: rawData.total_volumes.map(([timestamp, volume]) => ({
              timestamp,
              value: volume
            })),
            marketCaps: rawData.market_caps?.map(([timestamp, cap]) => ({
              timestamp,
              value: cap
            }))
          },
          metadata: {
            fetchedAt: new Date(),
            source: this.name,
            interval: this.getInterval(timeRange)
          }
        };
        
        return historicalData;
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    } catch (error) {
      throw this.handleError(error);
    }
  }
  
  /**
   * Search assets (basic implementation)
   */
  async searchAssets(
    query: string,
    type?: AssetType,
    config?: RequestConfig
  ): Promise<AssetSearchResult[]> {
    // Twelvedata doesn't have a comprehensive search API
    // Return known assets that match the query
    const knownAssets = this.getKnownAssets(type);
    const lowerQuery = query.toLowerCase();
    
    const matchedAssets = knownAssets.filter(asset => 
      asset.symbol.toLowerCase().includes(lowerQuery) ||
      asset.name.toLowerCase().includes(lowerQuery)
    );
    
    // If query looks like a symbol with exchange suffix (e.g., "0700.HK", "600519.SH", "000001.SH")
    // and no results found, try to validate it directly
    if (matchedAssets.length === 0 && /^[\dA-Z]+\.[A-Z]{2}$/i.test(query.trim())) {
      const symbol = query.trim().toUpperCase();
      const exchangeMatch = symbol.match(/\.([A-Z]{2})$/);
      const exchange = exchangeMatch ? exchangeMatch[1] : undefined;
      
      // Determine asset type based on exchange and symbol pattern
      let assetType: AssetType | undefined = type;
      if (!assetType) {
        // HK and SH stocks are typically stock type
        if (exchange === 'HK' || exchange === 'SH') {
          // Check if it's an index (typically starts with 000001 for SH indices)
          if (exchange === 'SH' && symbol.startsWith('000001')) {
            assetType = 'index';
          } else {
            assetType = 'stock';
          }
        }
      }
      
      if (assetType) {
        try {
          // Try to validate the symbol
          const validationAsset: Partial<Asset> = { symbol, type: assetType };
          if (exchange) {
            validationAsset.exchange = exchange;
          }
          const validation = await this.validateAsset(
            validationAsset,
            { ...config, timeout: 5000 }
          );
          
          if (validation.valid && validation.asset) {
            return [{
              id: validation.asset.id || symbol.toLowerCase().replace('.', '_'),
              symbol: validation.asset.symbol || symbol,
              name: validation.asset.name || symbol,
              type: validation.asset.type || assetType,
              ...(exchange && { exchange })
            }];
          }
        } catch (error) {
          // If validation fails, return empty results
          // This is expected for invalid symbols
        }
      }
    }
    
    return matchedAssets;
  }
  
  /**
   * Validate asset
   */
  async validateAsset(
    asset: Partial<Asset>,
    config?: RequestConfig
  ): Promise<AssetValidationResult> {
    try {
      if (!asset.symbol || !asset.type) {
        return {
          valid: false,
          error: 'Asset symbol and type are required'
        };
      }
      
      if (!this.supportedAssetTypes.includes(asset.type)) {
        return {
          valid: false,
          error: `Unsupported asset type: ${asset.type}`
        };
      }
      
      // Create test asset
      // For symbols with exchange suffix (e.g., 0700.HK, 600519.SH), preserve the format
      const symbol = asset.symbol.toUpperCase();
      const testAsset: Asset = {
        id: asset.id || symbol.toLowerCase().replace(/\./g, '_'),
        symbol: symbol, // Preserve original symbol format including exchange suffix
        name: asset.name || symbol,
        type: asset.type,
        priceStatus: 'loading',
        displayOrder: 0,
        isActive: true,
        ...(asset.exchange && { exchange: asset.exchange })
      };
      
      try {
        const priceData = await this.getAssetPrice(testAsset, { ...config, timeout: 5000 });
        
        // Return asset with price data
        return {
          valid: true,
          asset: {
            ...testAsset,
            price: priceData.current_price,
            change24h: priceData.price_change_24h,
            changePercent24h: priceData.price_change_percentage_24h,
            lastUpdated: new Date(priceData.last_updated),
            ...(priceData.source_url ? { sourceUrl: priceData.source_url } : {}),
            priceStatus: 'success' as const,
          }
        };
      } catch (error) {
        const err = error as { code?: string };
        if (err && typeof err === 'object' && err.code === 'AUTH_ERROR') {
          throw error;
        }
        return {
          valid: false,
          error: `Asset validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
          suggestions: await this.searchAssets(asset.symbol, asset.type)
        };
      }
    } catch (error) {
      return {
        valid: false,
        error: error instanceof Error ? error.message : 'Validation error'
      };
    }
  }
  
  /**
   * Test connection
   */
  async testConnection(config?: RequestConfig): Promise<boolean> {
    try {
      const result = await twelvedataService.testApiKey();
      return result.valid;
    } catch (error) {
      console.error('Twelvedata connection test failed:', error);
      return false;
    }
  }
  
  // Private helper methods
  
  private handleError(error: unknown): never {
    if (error instanceof Error) {
      // Check if it's already an ApiError
      if ('code' in error) {
        throw error;
      }
      
      // Handle network errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw createApiError(
          'NETWORK_ERROR',
          'Network connection failed',
          { provider: this.name, originalError: error }
        );
      }
      
      // Handle timeout errors
      if (error.name === 'AbortError' || error.message.includes('timeout')) {
        throw createApiError(
          'TIMEOUT_ERROR',
          'Request timed out',
          { provider: this.name, originalError: error }
        );
      }
      
      // Handle HTTP errors (if axios error)
      if ('response' in error && error.response) {
        const response = error.response as any;
        const status = response.status;
        const data = response.data;
        
        // Check if response body contains error (TwelveData sometimes returns 200 with error in body)
        if (data && typeof data === 'object') {
          if (status === 401 || ('code' in data && data.code === 401)) {
            throw createApiError(
              'AUTH_ERROR',
              data?.message || 'API key invalid or demo key limit exceeded',
              { provider: this.name, originalError: error }
            );
          }
          if ('code' in data && data.code === 404) {
            throw createApiError(
              'NOT_FOUND_ERROR',
              data.message || `Symbol not found: ${data.symbol || 'unknown'}`,
              { provider: this.name, originalError: error }
            );
          }
          if ('status' in data && data.status === 'error') {
            throw createApiError(
              'NOT_FOUND_ERROR',
              data.message || 'Invalid symbol or symbol not found',
              { provider: this.name, originalError: error }
            );
          }
        }
        
        const code = categorizeHttpError(status, data?.message);
        const retryAfter = response.headers ? extractRetryAfter(response.headers as Record<string, string>) : undefined;
        throw createApiError(
          code,
          data?.message || error.message,
          { provider: this.name, originalError: error, ...(retryAfter ? { retryAfter } : {}) }
        );
      }
      
      // Check if error message indicates symbol not found
      if (error.message.includes('Invalid symbol') || error.message.includes('not found') || error.message.includes('Twelvedata API error')) {
        throw createApiError(
          'NOT_FOUND_ERROR',
          error.message,
          { provider: this.name, originalError: error }
        );
      }
      
      // Generic error
      throw createApiError(
        'UNKNOWN_ERROR',
        error.message,
        { provider: this.name, originalError: error }
      );
    }
    
    throw createApiError(
      'UNKNOWN_ERROR',
      'An unknown error occurred',
      { provider: this.name, originalError: error }
    );
  }
  
  private groupAssetsByType(assets: Asset[]): Record<AssetType, Asset[]> {
    const groups: Record<AssetType, Asset[]> = {
      crypto: [],
      stock: [],
      index: [],
      forex: [],
      bond: [],
      futures: [],
      etf: [],
      fund: []
    };
    
    for (const asset of assets) {
      if (groups[asset.type]) {
        groups[asset.type].push(asset);
      }
    }
    
    return groups;
  }
  
  private parseForexSymbol(symbol: string): [string, string] {
    // Handle different forex symbol formats
    if (symbol.includes('/')) {
      const parts = symbol.split('/');
      if (parts.length >= 2 && parts[0] && parts[1]) {
        return [parts[0].trim(), parts[1].trim()];
      }
      throw new Error(`Invalid forex symbol format: ${symbol}`);
    }
    
    if (symbol.includes('_')) {
      const parts = symbol.split('_');
      if (parts.length >= 2 && parts[0] && parts[1]) {
        return [parts[0].trim(), parts[1].trim()];
      }
      throw new Error(`Invalid forex symbol format: ${symbol}`);
    }
    
    // Assume 6-character format like USDEUR
    if (symbol.length === 6) {
      return [symbol.substring(0, 3), symbol.substring(3)];
    }
    
    throw new Error(`Invalid forex symbol format: ${symbol}`);
  }
  
  private timeRangeToDays(timeRange: TimeRange): number {
    const rangeToDays: Record<TimeRange, number> = {
      '1D': 1,
      '7D': 7,
      '30D': 30,
      '90D': 90,
      '1Y': 365
    };
    
    return rangeToDays[timeRange] || 7;
  }
  
  private getInterval(timeRange: TimeRange): string {
    const rangeToInterval: Record<TimeRange, string> = {
      '1D': '1h',
      '7D': '4h',
      '30D': '1d',
      '90D': '1d',
      '1Y': '1d'
    };
    
    return rangeToInterval[timeRange] || '1d';
  }
  
  private getKnownAssets(type?: AssetType): AssetSearchResult[] {
    const allAssets: AssetSearchResult[] = [
      // Crypto
      { id: 'bitcoin', symbol: 'BTC', name: 'Bitcoin', type: 'crypto' },
      { id: 'ethereum', symbol: 'ETH', name: 'Ethereum', type: 'crypto' },
      { id: 'binancecoin', symbol: 'BNB', name: 'BNB', type: 'crypto' },
      { id: 'solana', symbol: 'SOL', name: 'Solana', type: 'crypto' },
      { id: 'cardano', symbol: 'ADA', name: 'Cardano', type: 'crypto' },
      { id: 'ripple', symbol: 'XRP', name: 'XRP', type: 'crypto' },
      { id: 'dogecoin', symbol: 'DOGE', name: 'Dogecoin', type: 'crypto' },
      { id: 'polkadot', symbol: 'DOT', name: 'Polkadot', type: 'crypto' },
      { id: 'polygon', symbol: 'MATIC', name: 'Polygon', type: 'crypto' },
      { id: 'avalanche-2', symbol: 'AVAX', name: 'Avalanche', type: 'crypto' },
      { id: 'chainlink', symbol: 'LINK', name: 'Chainlink', type: 'crypto' },
      { id: 'uniswap', symbol: 'UNI', name: 'Uniswap', type: 'crypto' },
      { id: 'litecoin', symbol: 'LTC', name: 'Litecoin', type: 'crypto' },
      { id: 'cosmos', symbol: 'ATOM', name: 'Cosmos', type: 'crypto' },
      
      // Stocks
      { id: 'appl', symbol: 'APPL', name: 'Apple Inc.', type: 'stock' },
      { id: 'msft', symbol: 'MSFT', name: 'Microsoft Corporation', type: 'stock' },
      { id: 'googl', symbol: 'GOOGL', name: 'Alphabet Inc. Class A', type: 'stock' },
      { id: 'goog', symbol: 'GOOG', name: 'Alphabet Inc. Class C', type: 'stock' },
      { id: 'amzn', symbol: 'AMZN', name: 'Amazon.com Inc.', type: 'stock' },
      { id: 'tsla', symbol: 'TSLA', name: 'Tesla Inc.', type: 'stock' },
      { id: 'nvda', symbol: 'NVDA', name: 'NVIDIA Corporation', type: 'stock' },
      { id: 'meta', symbol: 'META', name: 'Meta Platforms Inc.', type: 'stock' },
      { id: 'jpm', symbol: 'JPM', name: 'JPMorgan Chase & Co.', type: 'stock' },
      { id: 'v', symbol: 'V', name: 'Visa Inc.', type: 'stock' },
      { id: 'jnj', symbol: 'JNJ', name: 'Johnson & Johnson', type: 'stock' },
      { id: 'wmt', symbol: 'WMT', name: 'Walmart Inc.', type: 'stock' },
      { id: 'pg', symbol: 'PG', name: 'Procter & Gamble Co.', type: 'stock' },
      { id: 'ma', symbol: 'MA', name: 'Mastercard Inc.', type: 'stock' },
      { id: 'dis', symbol: 'DIS', name: 'The Walt Disney Company', type: 'stock' },
      { id: 'nflx', symbol: 'NFLX', name: 'Netflix Inc.', type: 'stock' },
      { id: 'amd', symbol: 'AMD', name: 'Advanced Micro Devices Inc.', type: 'stock' },
      { id: 'intc', symbol: 'INTC', name: 'Intel Corporation', type: 'stock' },
      { id: 'crm', symbol: 'CRM', name: 'Salesforce Inc.', type: 'stock' },
      { id: 'orcl', symbol: 'ORCL', name: 'Oracle Corporation', type: 'stock' },
      // Hong Kong Stocks
      { id: '0700_hk', symbol: '0700.HK', name: 'Tencent Holdings', type: 'stock', exchange: 'HK' },
      // A-Shares (China)
      { id: '600519_sh', symbol: '600519.SH', name: 'Kweichow Moutai', type: 'stock', exchange: 'SH' },
      
      // Indices
      { id: 'spx', symbol: 'SPX', name: 'S&P 500', type: 'index' },
      { id: 'djia', symbol: 'DJIA', name: 'Dow Jones Industrial Average', type: 'index' },
      { id: 'nasdaq', symbol: 'IXIC', name: 'NASDAQ Composite', type: 'index' },
      // China Indices
      { id: '000001_sh', symbol: '000001.SH', name: 'SSE Composite Index', type: 'index', exchange: 'SH' },
      
      // Forex
      { id: 'usd_eur', symbol: 'USD/EUR', name: 'US Dollar to Euro', type: 'forex' },
      { id: 'usd_gbp', symbol: 'USD/GBP', name: 'US Dollar to British Pound', type: 'forex' },
      { id: 'usd_jpy', symbol: 'USD/JPY', name: 'US Dollar to Japanese Yen', type: 'forex' },
      { id: 'usd_cny', symbol: 'USD/CNY', name: 'US Dollar to Chinese Yuan', type: 'forex' }
    ];
    
    return type ? allAssets.filter(asset => asset.type === type) : allAssets;
  }
}